<template>
  <div class="settings-page integration">
    <div class="title">
      <div>
        <img src="../../assets/directory.svg" class="header-icon" />
        <h2>Google Workspace Directory</h2>
      </div>
      <div class="actions">
        <BaseLink
          @click="
            openUrl('https://routine.co/integrations/services/google-workspace')
          "
        >
          <template #icon><ExternalIcon /></template>Help
        </BaseLink>
      </div>
    </div>

    <p class="description">
      Access profile information for users in your organization.
    </p>

    <section>
      <div v-if="gsuiteIdentities.length > 0">
        <div
          v-for="user in gsuiteIdentities"
          :key="`contact-${user.email}`"
          class="entry"
          :class="{
            disconnected:
              user.kind.provider === 'google' &&
              !user.kind.scopes.contact_directory,
          }"
        >
          <div class="account">
            <img src="../../assets/apps/google.svg" class="account-icon" />
            <label>{{ user.email }}</label>
          </div>
          <div v-if="user.kind.provider === 'google'" class="entry-actions">
            <BaseButton
              v-if="user.kind.scopes.contact_directory"
              kind="auxiliary"
              tint="red"
              text="Disconnect"
              :state="accountActionState(user.kind.identity)"
              @click="disconnectAccount(user.kind)"
            />
            <BaseButton
              v-else
              kind="main"
              type="secondary"
              text="Connect"
              :state="accountActionState(user.kind.identity)"
              @click="connectAccount(user.kind)"
            />
            <span
              class="removal-hint"
              v-tippy="{
                content: removalHint,
                arrow: false,
                placement: 'top',
              }"
            >
              <InfoIcon />
            </span>
          </div>
        </div>
      </div>

      <div v-else class="entry">
        <p class="no-accounts">
          None of your accounts are Google Workspace accounts. Only Google
          Workspace accounts (not regular Gmail accounts) can access the
          directory.
        </p>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";

import type { ProfileGoogle } from "@ctrl/controller-rpc";

import BaseButton, { type ButtonState } from "@/components/base/BaseButton.vue";
import BaseLink from "@/components/base/BaseLink.vue";
import ExternalIcon from "@/components/icons/ExternalIcon.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import * as inject from "@/components/inject";
import { useReleaser } from "@/ref";
import { openExternalUrl } from "@/utils/open-external-url";

import { notifyCheckBrowser } from "../utils";

const removalHint =
  "Disconnecting stops syncing but doesn't revoke Routine's access to this " +
  "Google account. To remove it completely, remove Routine from the " +
  "account's third-party apps & services in your Google settings.";

export default defineComponent({
  components: {
    BaseButton,
    BaseLink,
    ExternalIcon,
    InfoIcon,
  },
  props: {
    orgId: { type: String, required: true },
  },
  async setup() {
    const controller = inject.controller();

    const releaser = useReleaser();
    const identities = await releaser.add(controller.auth.profiles());

    // Gsuite identities (non-gmail accounts)
    const gsuiteIdentities = computed(() =>
      identities.value.filter(
        (profile) =>
          profile.kind.provider === "google" &&
          !profile.kind.identity.endsWith("@gmail.com")
      )
    );

    // Identity of the account whose connection is currently being toggled.
    const updatingAccount = ref<string | null>(null);

    // Request the directory scope for this account. If Google has not
    // granted it yet, requestScopes returns a consent URL that must be
    // opened in the browser; the profile updates after the user returns.
    async function connectAccount(kind: ProfileGoogle) {
      updatingAccount.value = kind.identity;
      try {
        notifyCheckBrowser(controller);
        const info = await controller.auth.requestScopes(
          kind.identity,
          { ...kind.scopes, contact_directory: true },
          window.location.href
        );
        if (info) {
          openExternalUrl(info.grant_uri, null, false);
          const onVisible = async () => {
            if (document.visibilityState === "visible") {
              document.removeEventListener("visibilitychange", onVisible);
              await controller.auth.refresh();
            }
          };
          document.addEventListener("visibilitychange", onVisible);
        } else {
          await controller.auth.refresh();
        }
      } finally {
        updatingAccount.value = null;
      }
    }

    // Drop the directory scope for this account. Once no account has it,
    // the integration goes back to "Available" in the catalog.
    async function disconnectAccount(kind: ProfileGoogle) {
      updatingAccount.value = kind.identity;
      try {
        await controller.auth.requestScopes(
          kind.identity,
          { ...kind.scopes, contact_directory: false },
          window.location.href
        );
      } finally {
        updatingAccount.value = null;
      }
    }

    const accountActionState = (identity: string): ButtonState =>
      updatingAccount.value === identity
        ? "loading"
        : updatingAccount.value !== null
          ? "disabled"
          : "default";

    const openUrl = (url: string) => openExternalUrl(url, controller);

    return {
      gsuiteIdentities,
      removalHint,
      accountActionState,
      connectAccount,
      disconnectAccount,
      openUrl,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.title {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  > div:first-child {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  h2 {
    margin-bottom: 0;
  }

  .actions {
    display: flex;
    gap: 8px;
  }
}

.header-icon {
  height: 32px;
  width: 32px;
}

.account {
  display: flex;
  align-items: center;
  gap: 8px;

  .account-icon {
    height: 20px;
    width: 20px;
  }
}

.entry.disconnected .account {
  .account-icon {
    opacity: 0.5;
  }

  label {
    @include themed() {
      color: t($grey-50);
    }
  }
}

.entry-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.removal-hint {
  display: inline-flex;
  align-items: center;
  cursor: default;

  :deep(svg) {
    width: 14px;
    height: 14px;
  }
}

.no-accounts {
  @extend %font-body-regular;
  margin: 0;

  @include themed() {
    color: t($grey-60);
  }
}
</style>
